<template>
    <div class="my-4">
        <h2 class="text-2xl flex items-center gap-2">
            {{ $t('Bestill kjøretimer eller kursplass') }}
        </h2>
        <p class="text-sm text-neutral-600 mt-1 max-w-2xl">{{ $t('Finn ledig tid og reserver tid på noen få klikk.') }}</p>
    </div>
    <div v-if="apiLoaded" class="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-2 gap-4 items-stretch">
        <div class="col-span-2 lg:col-span-1 space-y-4 order-last md:order-first">
            <div class="grid grid-cols-2 gap-4 text-black">
                <CategoryFilter :categories="calendarStore.categories" v-model="calendarStore.currentCategory" />
                <ProductFilter :products="calendarStore.products" v-model="calendarStore.currentProduct" :loading="loadingCalendarDays" />
            </div>
            <div class="rounded-3xl border-1 border-slate-200 p-4 bg-white">
                <FullCalendar ref="fullCalendar" :options="calendarOptions" />
            </div>
        </div>
        <div class="col-span-2 md:col-span-1 order-first md:order-last rounded-3xl border border-slate-200 p-6 bg-white space-y-4">
            
            <div class="flex items-center gap-1 text-slate-400 text-sm" v-if="loadingSlots">
                <LoaderCircle :size="14" class="animate-spin text-slate-400" />
                <span>{{ $t("Laster ledige tider...") }}</span>
            </div>
            <div v-if="showGuide">
                <h3 class="text-lg mb-2">Slik fungerer det</h3>
                <ol class="list-decimal list-inside text-sm text-neutral-700 space-y-1">
                    <li>Velg klasse og produkt.</li>
                    <li>Plukk en dato og et tidspunkt som passer deg.</li>
                    <li>Gå gjennom bestillingsskjema, så får du en bekreftelse på e-post.</li>
                </ol>
            </div>
            <div v-if="loaded && !teachers.length && !courses.length">
                <p class="text-sm text-neutral-600">{{ $t("Ingen ledige tider funnet for valgte dato og produkt.") }}</p>
            </div>
            <TeachersAndSlots v-if="teachers.length" :teachers="teachers" :currentProduct="currentProduct" :teacherImagePath="teacherImagePath" />
            <Courses v-if="courses.length" :courses="courses" :teacherImagePath="teacherImagePath" />
        </div>
    </div>
</template>

<script setup>
import axios from "axios";
import { ref, computed, watchEffect, useTemplateRef } from "vue";
import { useCalendarStore } from "./CalendarStore";

import FullCalendar from "@fullcalendar/vue3";
import CategoryFilter from "./CategoryFilter.vue";
import ProductFilter from "./ProductFilter.vue";
import TeachersAndSlots from "./TeachersAndSlots.vue";
import Courses from "./Courses.vue";

import { LoaderCircle } from "lucide-vue-next";

const calendarStore = useCalendarStore();

const loadingCalendarDays = ref(false);
const loadingSlots = ref(false);
const loaded = ref(false);
const showGuide = computed(() => !loadingSlots.value && !teachers.value.length && !courses.value.length);
const teacherImagePath = ref('');
const teachers = ref([]);
const courses = ref([]);

function selectDay(dateStr) {
    console.log("Selected date:", dateStr);

    document.querySelectorAll(".fc-day.fc-selected").forEach((el) => {
        el.classList.remove("fc-selected");
    });
    const clickedDate = document.querySelector('.fc-daygrid-day[data-date="' + dateStr + '"]');
    if (clickedDate) {
        clickedDate.classList.add("fc-selected");
    }
}

async function getSlots(dateStart, dateEnd) {
    loadingSlots.value = true;

    if (!dateEnd) {
        dateEnd = dateStart;
    }

    const formData = new FormData();
    formData.append("product_id", TABS.calendar.filter.product_id);
    formData.append("web_category_id", TABS.calendar.filter.web_category_id);
    formData.append("office_id", TABS.calendar.filter.office_id);
    formData.append("product_type", TABS.calendar.filter.product_type);
    formData.append("start", dateStart);
    formData.append("end", dateEnd);
    formData.append("json", true);

    const response = await axios.post(TABS.api.getHost() + '/calendar/event_list/', formData);
    teacherImagePath.value = response.data.teacherImagePath;
    teachers.value = response.data.teachers || [];
    courses.value = response.data.courses || [];
    loadingSlots.value = false;
    
}

// Refs
const fullCalendar = useTemplateRef("fullCalendar");
const apiLoaded = computed(() => calendarStore.apiLoaded);
const currentProduct = computed(() => calendarStore.currentProduct);
const calendarOptions = computed(() => {
    const options = calendarStore.calendarOptions;

    options.loading = function (isLoading) {
        loadingCalendarDays.value = isLoading;
    };
    options.dateClick = function (args) {
        selectDay(args.dateStr);
        getSlots(args.dateStr);
    };
    options.eventClick = function (args) {
        console.log("Date clicked:", args.event.startStr);
        selectDay(args.event.startStr);
        getSlots(args.event.startStr);
    };
    options.eventContent = function (arg) {
        let slotCount = arg.event.extendedProps.slotCount || arg.event.extendedProps.available_places || 0;

        return {
            html: `
                    <div class="fc-event-content">
                        <div class="fc-event-title">${arg.event.title}</div>
                        ${slotCount > 0 ? `<div class="fc-slot-count">${slotCount > 3 ? "3+" : slotCount}</div>` : ""}
                    </div>
                `,
        };
    };
    options.dayCellContent = function (arg) {
        const dayNumber = arg.dayNumberText.replace(".", "");
        return {
            html: `
                        <div class="fc-daygrid-day-top">
                            <div class="fc-signal"></div>
                            <div class="fc-mark">${dayNumber}</div>
                        </div>
                    `,
        };
    };
    options.eventDidMount = function (val) {
        const fcDaygridDayFrame = val.el.closest(".fc-daygrid-day-frame");
        if (!fcDaygridDayFrame) {
            return;
        }
        const daygridDayTop = fcDaygridDayFrame.querySelector(".fc-daygrid-day-top");
        if (!daygridDayTop) {
            return;
        }
        if (val.event.extendedProps.isHolidayEvent) {
            daygridDayTop.classList.add("fc-holiday-event");
        } else {
            daygridDayTop.classList.add("fc-bookable");
        }
    };

    return options;
});

watchEffect(() => {
    if (!currentProduct.value) {
        return;
    }

    refreshCalendarEvents();
});

function refreshCalendarEvents() {
    if (!fullCalendar.value) {
        return;
    }
    TABS.calendar.filter.product_id = currentProduct.value.product_id;
    TABS.calendar.filter.web_category_id = currentProduct.value.web_category_id;
    TABS.calendar.filter.office_id = window.officeId;
    TABS.calendar.filter.product_type = currentProduct.value.product_type;

    fullCalendar.value.getApi().refetchEvents();
}

</script>
